import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { formatCurrency } from '../utils/currency';
import { getTranslation, setTranslation, validateTranslations } from '../utils/translations';

const Inventory = () => {
  const { t, i18n } = useTranslation();
  const { token, API } = useContext(AuthContext);
  const [ingredients, setIngredients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ translations: {} });

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      const res = await axios.get(`${API}/ingredients`);
      setIngredients(res.data);
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate translations: require at least one language with a name
    if (!validateTranslations(formData.translations, 'name')) {
      toast.error(t('messages.provideTranslation'));
      return;
    }
    try {
      await axios.post(`${API}/ingredients`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.success'));
      setDialogOpen(false);
      setFormData({ translations: {} });
      fetchIngredients();
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  return (
    <div className="inventory">
      <div className="section-header">
        <h2 className="section-title">{t('inventory.inventory')}</h2>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-ingredient-btn">
          <Plus size={16} /> Add Ingredient
        </Button>
      </div>

      <div className="card mt-4">
        <table>
          <thead>
            <tr>
              <th>{t('menu.itemName')}</th>
              <th>{t('inventory.stock')}</th>
              <th>{t('inventory.minStock')}</th>
              <th>{t('inventory.unit')}</th>
              <th>{t('inventory.costPerUnit')}</th>
              <th>{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {ingredients.map((ing) => (
              <tr key={ing.id} data-testid={`ingredient-row-${ing.id}`}>
                <td data-testid="ingredient-name">{getTranslation(ing.translations, i18n.language || 'en', 'name')}</td>
                <td data-testid="ingredient-stock">{ing.stock}</td>
                <td>{ing.min_stock}</td>
                <td>{ing.unit}</td>
                <td>{formatCurrency(ing.cost_per_unit)}</td>
                <td>
                  {ing.stock <= ing.min_stock && (
                    <span className="badge badge-danger" data-testid="low-stock-badge">
                      <AlertTriangle size={12} /> {t('inventory.lowStock')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="ingredient-dialog" aria-describedby="ingredient-dialog-description">
          <DialogHeader>
            <DialogTitle>Add Ingredient</DialogTitle>
            <p id="ingredient-dialog-description" className="text-sm text-gray-500">
              Add a new ingredient to the inventory by filling in the details below.
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label>Name (EN)</label>
              <input
                type="text"
                value={getTranslation(formData.translations, 'en', 'name')}
                onChange={(e) => setFormData({ ...formData, translations: setTranslation(formData.translations, 'en', 'name', e.target.value) })}
                required
                data-testid="ingredient-name-en-input"
              />
            </div>
            <div className="form-group">
              <label>Name (TR)</label>
              <input
                type="text"
                value={getTranslation(formData.translations, 'tr', 'name')}
                onChange={(e) => setFormData({ ...formData, translations: setTranslation(formData.translations, 'tr', 'name', e.target.value) })}
                required
                data-testid="ingredient-name-tr-input"
              />
            </div>
            <div className="form-group">
              <label>{t('inventory.unit')}</label>
              <select
                value={formData.unit || ''}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                required
                data-testid="ingredient-unit-select"
              >
                <option value="">Select...</option>
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="l">l</option>
                <option value="ml">ml</option>
                <option value="piece">piece</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('inventory.stock')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.stock || ''}
                onChange={(e) => setFormData({ ...formData, stock: parseFloat(e.target.value) })}
                required
                data-testid="ingredient-stock-input"
              />
            </div>
            <div className="form-group">
              <label>{t('inventory.minStock')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.min_stock || ''}
                onChange={(e) => setFormData({ ...formData, min_stock: parseFloat(e.target.value) })}
                required
                data-testid="ingredient-min-stock-input"
              />
            </div>
            <div className="form-group">
              <label>{t('inventory.costPerUnit')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.cost_per_unit || ''}
                onChange={(e) => setFormData({ ...formData, cost_per_unit: parseFloat(e.target.value) })}
                required
                data-testid="ingredient-cost-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="save-ingredient-btn">{t('actions.save')}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <style>{`
        .inventory {
          width: 100%;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
        }

        .form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 13px;
          font-weight: 600;
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default Inventory;
